import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Mail } from "lucide-react";

import {
  useSendPacket,
  useSendPacketPreview,
  type RecipientKind,
  type SendPacketRecipient,
} from "@/admin/pages/claims/use-send-packet";
import type { CommunicationHold } from "@/admin/hooks/use-claims";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";

// Recipient wording is served by the preview endpoint, read from the tenant's
// own entity labels, so a tenant who calls their customer a Homeowner sees
// "Homeowner" here without a deploy. These are the last-resort fallbacks for a
// kind the backend did not label.
const KIND_LABEL_FALLBACKS: Record<string, string> = {
  consumer: "Customer",
  contractor: "Contractor",
  third_party: "Someone else",
};

const KIND_HINTS: Record<string, string> = {
  consumer: "The customer record attached to this claim.",
  contractor: "The contractor (installer / repairer) on this claim.",
  third_party: "Type any email - yourself, an external lab, etc.",
};

interface ThirdPartyDraft {
  email: string;
  name: string;
}

/**
 * Manual "Send packet" dialog.
 *
 * The template is system-picked - we only show its name. Admin picks
 * one or more recipients to send the same packet to. Third party kind
 * needs admin-supplied email; consumer/contractor are resolved from
 * the claim on the backend.
 */
export function SendPacketDialog({
  open,
  onOpenChange,
  claimUuid,
  communicationHold,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  claimUuid: string;
  /**
   * WPS-145: the claim's communication hold, when the tenant configures
   * one. While it is on, automated messaging about the claim is paused -
   * but a manual send is the channel the hold hands control to, so this
   * warns rather than blocks.
   */
  communicationHold?: CommunicationHold | null;
}) {
  const { t } = useTranslation();
  const previewQuery = useSendPacketPreview(open ? claimUuid : undefined);
  const sendPacket = useSendPacket(claimUuid);

  const [selectedKinds, setSelectedKinds] = useState<Set<RecipientKind>>(
    new Set(),
  );
  const [thirdParty, setThirdParty] = useState<ThirdPartyDraft>({
    email: "",
    name: "",
  });
  const [alsoSms, setAlsoSms] = useState(false);

  // Wrap the parent's onOpenChange so closing the dialog also resets
  // the form. Doing this at the event-handler level (rather than in a
  // useEffect on `open`) keeps the state synchronisation explicit and
  // avoids the cascading-renders lint rule.
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setSelectedKinds(new Set());
      setThirdParty({ email: "", name: "" });
      setAlsoSms(false);
    }
    onOpenChange(next);
  };

  const preview = previewQuery.data;
  const loading = previewQuery.isLoading;

  const toggleKind = (kind: RecipientKind) => {
    setSelectedKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  };

  const canSubmit = (() => {
    if (sendPacket.isPending) return false;
    // Allow an SMS-only send (no email recipients picked).
    if (selectedKinds.size === 0 && !alsoSms) return false;
    if (selectedKinds.has("third_party") && !thirdParty.email.trim())
      return false;
    return true;
  })();

  const handleSubmit = async () => {
    const recipients: SendPacketRecipient[] = [];
    for (const kind of selectedKinds) {
      if (kind === "third_party") {
        recipients.push({
          kind,
          email: thirdParty.email.trim(),
          name: thirdParty.name.trim() || undefined,
        });
      } else {
        recipients.push({ kind });
      }
    }
    try {
      await sendPacket.mutateAsync({ recipients, send_sms: alsoSms });
      handleOpenChange(false);
    } catch {
      // toast handled in the hook
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Send email packet</DialogTitle>
          <DialogDescription>
            {loading
              ? "Resolving the packet for this claim…"
              : preview
                ? `The system will send the “${preview.template.name}” packet, chosen automatically based on this claim's current state and defect.`
                : "No email packet is configured for this claim. Author an email trigger first to enable manual sending."}
          </DialogDescription>
        </DialogHeader>

        {communicationHold?.active ? (
          <div className="rounded-md border-[0.5px] border-l-[3px] border-[var(--rule)] border-l-[var(--status-warn)] bg-[var(--bg-light)] px-3 py-2 text-[12px] text-[var(--ink-2)]">
            {t(
              "claims.communicationHold.sendPacketWarning",
              "{{label}} is on for this claim, so automated messages are paused. Sending from here still goes out - send it only if you mean to.",
              { label: communicationHold.label },
            )}
          </div>
        ) : null}

        {preview && (
          <div className="space-y-4 py-2">
            <div className="rounded-md border border-[var(--rule)] bg-[var(--bg-light)] px-3 py-2">
              <div className="text-[11px] uppercase tracking-wider text-[var(--ink-3)]">
                Will send
              </div>
              <div className="text-sm font-medium text-[var(--ink)]">
                {preview.template.name}
              </div>
              <div className="text-xs text-[var(--ink-3)] font-mono mt-0.5">
                {preview.template.subject}
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-[var(--ink-3)]">
                Send to
              </Label>
              <div className="mt-2 space-y-2">
                {preview.available_recipient_kinds.map((kind) => {
                  const checked = selectedKinds.has(kind);
                  return (
                    <Button
                      key={kind}
                      type="button"
                      variant="outline"
                      onClick={() => toggleKind(kind)}
                      className={`h-auto w-full justify-start whitespace-normal rounded-md border p-3 text-left transition-all ${
                        checked
                          ? "border-[var(--ink)] bg-[var(--bg-light)] hover:bg-[var(--bg-light)]"
                          : "border-[var(--rule)] bg-transparent hover:border-[var(--rule-strong)] hover:bg-transparent"
                      }`}
                    >
                      <div className="flex w-full items-start gap-2">
                        <span
                          className={`mt-0.5 h-4 w-4 rounded border flex items-center justify-center shrink-0 ${
                            checked
                              ? "bg-[var(--ink)] border-[var(--ink)]"
                              : "border-[var(--rule-strong)]"
                          }`}
                        >
                          {checked && (
                            <svg
                              viewBox="0 0 14 14"
                              className="size-2.5 text-[var(--bg)]"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                            >
                              <path d="M2 7l3 3 7-7" />
                            </svg>
                          )}
                        </span>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-[var(--ink)]">
                            {previewQuery.data?.recipient_labels?.[kind] ??
                              KIND_LABEL_FALLBACKS[kind] ??
                              kind}
                          </div>
                          <div className="text-xs text-[var(--ink-3)] mt-0.5">
                            {KIND_HINTS[kind] ?? "Pick from the resource."}
                          </div>
                        </div>
                      </div>
                    </Button>
                  );
                })}
              </div>
            </div>

            {selectedKinds.has("third_party") && (
              <div className="rounded-md border border-[var(--rule)] bg-[var(--bg)] p-3 space-y-2">
                <div>
                  <Label htmlFor="third-party-email" className="text-xs">
                    Email
                  </Label>
                  <Input
                    id="third-party-email"
                    type="email"
                    placeholder="lab@external.com"
                    value={thirdParty.email}
                    onChange={(e) =>
                      setThirdParty((p) => ({ ...p, email: e.target.value }))
                    }
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="third-party-name" className="text-xs">
                    Name (optional)
                  </Label>
                  <Input
                    id="third-party-name"
                    placeholder="External Lab"
                    value={thirdParty.name}
                    onChange={(e) =>
                      setThirdParty((p) => ({ ...p, name: e.target.value }))
                    }
                    className="mt-1"
                  />
                </div>
              </div>
            )}

            {preview.sms_available && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => setAlsoSms((v) => !v)}
                className={`w-full text-left rounded-md border p-3 h-auto justify-start font-normal transition-all ${
                  alsoSms
                    ? "border-[var(--ink)] bg-[var(--bg-light)] hover:bg-[var(--bg-light)]"
                    : "border-[var(--rule)] hover:border-[var(--ink-3)] hover:bg-transparent"
                }`}
              >
                <div className="flex items-start gap-2">
                  <span
                    className={`mt-0.5 h-4 w-4 rounded border flex items-center justify-center shrink-0 ${
                      alsoSms
                        ? "bg-[var(--ink)] border-[var(--ink)]"
                        : "border-[var(--rule-strong)]"
                    }`}
                  >
                    {alsoSms && (
                      <svg
                        viewBox="0 0 14 14"
                        className="h-2.5 w-2.5 text-[var(--bg)]"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <path d="M2 7l3 3 7-7" />
                      </svg>
                    )}
                  </span>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-[var(--ink)]">
                      Also text the customer
                    </div>
                    <div className="text-xs text-[var(--ink-3)] mt-0.5 whitespace-normal">
                      Sends the upload link by SMS (needs the customer's mobile
                      number and SMS opt-in).
                    </div>
                  </div>
                </div>
              </Button>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={sendPacket.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || !preview}
            className="gap-1.5"
          >
            {sendPacket.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Mail className="h-3.5 w-3.5" />
            )}
            {sendPacket.isPending ? "Sending…" : "Send packet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
